import { useAppDispatch, useAppSelector } from "@app/hooks";
import { LCheckbox, LInputField, LRadio } from "@components";
import { DropdownMenuOption, LDropdownMenu } from "@components/LDropdownMenu";
import { LSelect, SelectOption } from "@components/LSelect";
import { monthNames } from "@helpers/dateHelpers";
import { DefaultErrorMessages } from "@helpers/formValidationHelper";
import { getDropdownLabel } from "@helpers/stringHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import { useModal } from "@services/ModalContext";
import { Sex, RaceOptions, EthnicityOptions } from "@store/generalTypes";
import classNames from "classnames";
import { isArray } from "lodash";
import { useEffect, useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { array, mixed, object, SchemaOf, string } from "yup";
import { ExamineeDetails, NewExamineeStepNames, updateExamineeDetails } from "../../store/newExamineeSlice";
import { ExamineeCard } from "../ExamineeCard";

const formSchema: SchemaOf<ExamineeDetails> = object({
    sex: mixed().oneOf(Object.values(Sex)).required(),
    birthDay: string()
        .required(DefaultErrorMessages.required)
        .test("dayRange", "Invalid Day", (val) => /\b(0?[1-9]|[12][0-9]|3[01])\b/.test(val!)),
    birthMonth: string().required(DefaultErrorMessages.required),
    birthYear: string()
        .required(DefaultErrorMessages.required)
        .min(4, "Enter a valid year")
        .test("yearRange", "Enter a valid year", (val) => {
            const valAsNumber = Number(val);
            return valAsNumber >= 1900 && valAsNumber < new Date().getFullYear();
        }),
    race: mixed().optional(),
    ethnicity: mixed().optional(),
    highestLevelEducation: string().optional(),
    primaryLanguage: string().required(DefaultErrorMessages.required),
    secondaryLanguages: array().of(string()).optional()
});

const months: SelectOption[] = monthNames.map((m, idx) => ({ name: m, value: `${idx + 1}` }));

// TODO: check for actual values
export const educationOptions = [
    { name: "No formal education" } as SelectOption,
    { name: "Primary education" } as SelectOption,
    { name: "Secondary education or high school" } as SelectOption,
    { name: "Vocational qualification" } as SelectOption,
    { name: "Bachelor's degree" } as SelectOption,
    { name: "Master's degree" } as SelectOption,
    { name: "Doctorate or higher" } as SelectOption
];

export const primaryLanguageOptions = [
    { name: "English" },
    { name: "Spanish" },
    { name: "Portuguese" },
    { name: "Arabic" },
    { name: "French" },
    { name: "German" },
    { name: "Japanese" },
    { name: "Korean" },
    { name: "Mandarin" },
    { name: "Russian" },
    { name: "Other" }
] as SelectOption[];

export const ExamineeDetailsForm = (): JSX.Element => {
    const validateMethods = useForm<ExamineeDetails>({
        resolver: yupResolver(formSchema)
    });

    const { setFormName, onConfirm } = useModal();
    const dispatch = useAppDispatch();

    const contactInfo = useAppSelector((x) => x.newExaminee.contactInfo);
    const currentDetails = useAppSelector((x) => x.newExaminee.examineeDetails);

    const onSubmit = (data: ExamineeDetails): void => {
        dispatch(updateExamineeDetails(data));
        onConfirm?.();
    };

    const labelClasses = classNames("label", "is-small", "mt-5");

    const errors = validateMethods.formState.errors;

    const primaryLanguage = validateMethods.watch().primaryLanguage;
    const secondaryLanguages = validateMethods.watch().secondaryLanguages as string[] | string | undefined;

    const selectedEthnicity = EthnicityOptions.find((x) => x.value === currentDetails?.ethnicity)?.name;

    const selectedRace = RaceOptions.find((x) => x.value === currentDetails?.race)?.name;

    const secondaryLanguageOptions = useMemo(
        () =>
            primaryLanguageOptions
                ?.filter(({ name }) => name !== primaryLanguage)
                .map(({ name }) => ({
                    optionName: "Secondary Language",
                    children: (
                        <LCheckbox
                            inputName="secondaryLanguages"
                            label={name}
                            value={name}
                            defaultChecked={currentDetails?.secondaryLanguages?.includes(name)}
                        />
                    )
                })) as DropdownMenuOption[],
        [primaryLanguage]
    );

    useEffect(() => {
        if (
            primaryLanguage !== undefined &&
            ((isArray(secondaryLanguages) && secondaryLanguages?.includes(primaryLanguage)) ||
                secondaryLanguages === primaryLanguage)
        ) {
            validateMethods.resetField("secondaryLanguages");
        }
    }, [primaryLanguage]);

    useEffect(() => {
        setFormName?.(NewExamineeStepNames.Details);
        validateMethods.setValue("secondaryLanguages", []);
        if (currentDetails) {
            void validateMethods.trigger();
        }
    }, []);

    return (
        <div className="container has-text-ldark-gray pl-5" style={{ minWidth: "55vw" }}>
            <div className="columns">
                <div className="column">
                    <p className="my-2 mb-2">Please provide some necessary background information</p>
                    <FormProvider {...validateMethods}>
                        <form id={NewExamineeStepNames.Details} onSubmit={validateMethods.handleSubmit(onSubmit)}>
                            {/* SEX */}
                            <label className={labelClasses}>1. Sex</label>
                            <div>
                                <LRadio
                                    inputName="sex"
                                    id={Sex.Male}
                                    value={Sex.Male}
                                    defaultChecked={(currentDetails?.sex as any) === Sex.Male}
                                    shouldCap
                                />
                                <div className="is-flex is-flex-direction-row">
                                    <LRadio
                                        inputName="sex"
                                        id={Sex.Female}
                                        value={Sex.Female}
                                        defaultChecked={(currentDetails?.sex as any) === Sex.Female}
                                        shouldCap
                                    />
                                    <LRadio
                                        inputName="sex"
                                        id={Sex.Other}
                                        value={Sex.Other}
                                        defaultChecked={(currentDetails?.sex as any) === Sex.Other}
                                        shouldCap
                                    />
                                </div>
                                {errors.sex && <p className="help is-danger">{DefaultErrorMessages.required}</p>}
                            </div>

                            {/* BIRTHDATE */}
                            <label className={labelClasses}>2. Birthdate</label>
                            <div className="columns">
                                <div className="column is-4">
                                    <LSelect
                                        options={months}
                                        label="Month"
                                        inputName="birthMonth"
                                        defaultOption={{ name: "Select Month", value: "" } as SelectOption}
                                        defaultValue={currentDetails?.birthMonth}
                                    />
                                </div>
                                <div className="column is-3">
                                    <LInputField
                                        label="Day"
                                        inputName="birthDay"
                                        placeholder="DD"
                                        maxLength={2}
                                        isNumber
                                        defaultValue={currentDetails?.birthDay}
                                    />
                                </div>
                                <div className="column">
                                    <LInputField
                                        label="Year"
                                        inputName="birthYear"
                                        placeholder="YYYY"
                                        maxLength={4}
                                        isNumber
                                        defaultValue={currentDetails?.birthYear}
                                    />
                                </div>
                            </div>

                            {/* RACE */}
                            <div className="mt-5">
                                <LSelect
                                    options={RaceOptions}
                                    label="3. Race (Optional)"
                                    inputName="race"
                                    defaultOption={{ name: "Select race", value: "0" } as SelectOption}
                                    defaultValue={selectedRace}
                                />
                            </div>

                            {/* ETHNICITY */}
                            <div className="mt-5">
                                <LSelect
                                    options={EthnicityOptions}
                                    label="4. Ethnicity (Optional)"
                                    inputName="ethnicity"
                                    defaultOption={{ name: "Select ethnicity", value: "0" } as SelectOption}
                                    defaultValue={selectedEthnicity}
                                />
                            </div>

                            {/* LEVEL OF EDUCATION */}
                            <div className="mt-5">
                                <LSelect
                                    options={educationOptions}
                                    label="5. Highest Level of Education (Optional)"
                                    inputName="highestLevelEducation"
                                    defaultOption={{ name: "Select level", value: "" } as SelectOption}
                                    defaultValue={currentDetails?.highestLevelEducation}
                                />
                            </div>

                            {/* PRIMARY LANGUAGE */}
                            <div className="mt-5">
                                <LSelect
                                    options={primaryLanguageOptions}
                                    label="6. Primary Language"
                                    inputName="primaryLanguage"
                                    defaultOption={{ name: "Select language", value: "" } as SelectOption}
                                    defaultValue={currentDetails?.primaryLanguage}
                                />
                            </div>

                            {/* SECONDARY LANGUAGE */}
                            {primaryLanguage && (
                                <div className="mt-5 mb-6 pb-6">
                                    <LDropdownMenu
                                        id="secondaryLanguagesOptions"
                                        label={getDropdownLabel(secondaryLanguages)}
                                        tag="7. Secondary Language (Optional)"
                                        options={secondaryLanguageOptions}
                                        theme="light"
                                        variation="select"
                                        iconFamily="angle"
                                        iconColor={"#0028BD"}
                                        iconSize={"small"}
                                    />
                                </div>
                            )}
                        </form>
                    </FormProvider>
                </div>
                <div className="column">
                    <ExamineeCard
                        bgColor="#F0F7FA"
                        contactInfo={contactInfo}
                        examineeDetails={validateMethods.watch()}
                        classList="p-6 mx-5"
                    />
                </div>
            </div>
        </div>
    );
};
